import { ImageCreateProps, ImageCreateResponseProps, ImageToImageEditProps, ImageToImageEditResponseProps } from "../types/ai/api";
import { ApiPortalBackend, EstimateRunCostResult } from "./ApiPortalBackend";

// null = SL-CostsService could not estimate (unreachable/not configured, or estimated:false) -
// UI must render "—" for null, never 0. A real 0 (free function) is a valid, non-null result.
const toCredits = (result: EstimateRunCostResult | null): number | null =>
    result && result.estimated ? Math.round(result.credits) : null;

// Was entirely mocked (Math.random()) until 2026-07-16, basePath pointed at PDF_API_URL (wrong
// service, likely copy-paste) instead of PortalBackend - see TASKS.md Task #92.
export class ApiPayment {
    // "image_gen" (credits = 400 * (width*height)/(640*640), see SL-CostsService/API.md). Der
    // Pro-Bild-Faktor wird seit 2026-07-22 server-seitig ueber count_param angewendet (count ×
    // Einzelpreis) - deshalb senden wir count = numberOfGenerations und multiplizieren NICHT mehr
    // clientseitig (Single Source of Truth). count <= 0 -> 422, daher nur > 0 senden.
    static async GetCostsForImageCreate(data: ImageCreateProps): Promise<ImageCreateResponseProps> {
        return { costs: toCredits(await ApiPortalBackend.EstimateRunCost({
            type: 'image_gen',
            width: data.imgWidth,
            height: data.imgHeight,
            count: data.numberOfGenerations > 0 ? data.numberOfGenerations : undefined,
        })) };
    }

    // Video generation is a single fixed-price RunType (video_gen) in SL-CostsService right now -
    // no resolution/duration/frame parameters yet (fixed price, currently 2400 credits). Same
    // single-source-of-truth pattern as the image estimates; null on failure so the UI shows "—".
    // When video_gen becomes parametrized server-side, extend this to pass those params (and the
    // callers to compute them), mirroring GetCostsForImageCreate.
    static async GetCostsForVideoCreate(): Promise<{ costs: number | null }> {
        return { costs: toCredits(await ApiPortalBackend.EstimateRunCost({ type: 'video_gen' })) };
    }

    // The 11 image-editing functions got real per-function prices in SL-CostsService on 2026-07-21
    // (Task #92 entblockt). One EstimateRunCost call per function so SL-CostsService stays the
    // single source of truth (no hard-coded prices here), same pattern as GetCostsForImageCreate.
    // A 0 result means the function is free (e.g. upscale, remove background); null means the
    // estimate failed and the UI must show "—" (never guess). Called in parallel to keep latency low.
    static async GetCostsForImageToImageEdit(data: ImageToImageEditProps): Promise<ImageToImageEditResponseProps> {
        const width = data.imgWidth;
        const height = data.imgHeight;

        // image_expand is priced by expansion FACTOR (tiered), not pixel count. Derive the factor
        // from how much larger the padded canvas is vs. the original reference (imgWidth/imgHeight
        // already include the expand padding, see SettingsSidebar responseImageDimension). Falls
        // back to 1 when no reference is known. See the EstimateRunCost cross-repo NOTE: this stays
        // inaccurate until PortalBackend's buildCostParams() forwards factor for image_expand.
        const originalArea = data.reference ? data.reference.width * data.reference.height : 0;
        const expandFactor = originalArea > 0 ? Math.max(1, (width * height) / originalArea) : 1;

        // Pro-Variation-Faktor (count_param, server-seitig): count = numberOfGenerations NUR fuer die
        // Funktionen, die tatsaechlich N Varianten erzeugen. Die Einzel-Output-Funktionen (Upscale,
        // Remove Background, Spell Correction, Extract Object/Pose) senden KEIN count -> Faktor 1,
        // damit die Vorschau dem tatsaechlichen Abzug entspricht (Nutzer-Entscheid 2026-07-22, aus
        // den Funktions-Hilfetexten abgeleitet). count <= 0 -> 422, daher nur > 0 senden.
        const count = data.numberOfGenerations > 0 ? data.numberOfGenerations : undefined;

        const [
            convertToImage, spellCorrection, generateVariations, extractObject, extractPose,
            expand, extractAndRemove, removeBackground, removeObject, upscale2x, upscale4x,
        ] = await Promise.all([
            // skalieren mit der Variationszahl:
            ApiPortalBackend.EstimateRunCost({ type: 'image_convert_to_image', width, height, count }),
            // Einzel-Output (kein count):
            ApiPortalBackend.EstimateRunCost({ type: 'image_spell_correction' }),
            // skaliert:
            ApiPortalBackend.EstimateRunCost({ type: 'image_generate_variations', width, height, count }),
            // Einzel-Output:
            ApiPortalBackend.EstimateRunCost({ type: 'image_extract_object' }),
            ApiPortalBackend.EstimateRunCost({ type: 'image_extract_pose' }),
            // skalieren:
            ApiPortalBackend.EstimateRunCost({ type: 'image_expand', factor: expandFactor, count }),
            ApiPortalBackend.EstimateRunCost({ type: 'image_extract_and_remove', count }),
            // Einzel-Output:
            ApiPortalBackend.EstimateRunCost({ type: 'image_remove_background' }),
            // skaliert:
            ApiPortalBackend.EstimateRunCost({ type: 'image_remove_object', count }),
            // Einzel-Output:
            ApiPortalBackend.EstimateRunCost({ type: 'image_upscale_2x' }),
            ApiPortalBackend.EstimateRunCost({ type: 'image_upscale_4x' }),
        ]);

        return {
            convertToImage: toCredits(convertToImage),
            spellCorrection: toCredits(spellCorrection),
            generateVariations: toCredits(generateVariations),
            extractObject: toCredits(extractObject),
            extractPose: toCredits(extractPose),
            expand: toCredits(expand),
            extractAndRemove: toCredits(extractAndRemove),
            removeBackground: toCredits(removeBackground),
            removeObject: toCredits(removeObject),
            upscale2x: toCredits(upscale2x),
            upscale4x: toCredits(upscale4x),
        };
    }
}